import React, { useEffect, useRef, useState, useMemo } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useDispatch } from "react-redux";
import { logoutUser } from "../../store/auth/actions";
import { Modal } from "react-bootstrap";
import { LocalStorageKeys } from "../../utils/LocalStorageUtils";

const MAX_IDLE_TIME = 1000 * 60 * 60 * 7.55; // 7.55 hours
const SHORT_INTERVAL = 1000 * 60 * 1; // 1 minute
const PROMPT_BEFORE_LOGOUT = 1000 * 60 * 5; // 5 minutes before final timeout
const COUNTDOWN_SECONDS = 300;

const UserActivity: React.FC = () => {
  const dispatch = useDispatch();
  const [remaining, setRemaining] = useState<number>(COUNTDOWN_SECONDS);
  const [open, setOpen] = useState<boolean>(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [accumulatedIdleTime, setAccumulatedIdleTime] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleLogout = (autoLogout?: boolean) => {
    if (autoLogout) {
      setOpen(true);
      const id = setTimeout(() => {
        sessionStorage.clear();
        localStorage.clear();
        dispatch(logoutUser() as any);
        setOpen(false);
        window.location.reload();
      }, PROMPT_BEFORE_LOGOUT);
      setTimeoutId(id);
    } else {
      sessionStorage.clear();
      localStorage.clear();
      dispatch(logoutUser() as any);
      window.location.reload();
    }
  };

  const handleStayLoggedIn = () => {
    setOpen(false);
    setRemaining(COUNTDOWN_SECONDS);
    setAccumulatedIdleTime(0);
    startInterval();
    activate();
    reset();
    if (timeoutId) clearTimeout(timeoutId);
  };

  const handlePromptLogout = () => {
    localStorage.setItem(LocalStorageKeys.UserIdleState, "idle");
    setOpen(false);
  };

  const {
    activate,
    isLastActiveTab,
    isPrompted,
    reset,
  } = useIdleTimer({
    onIdle: handlePromptLogout,
    timeout: PROMPT_BEFORE_LOGOUT,
    crossTab: true,
    leaderElection: false,
    syncTimers: 200,
    throttle: 500,
  });

  // Start short interval to simulate long idle timeout
  const startInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setAccumulatedIdleTime((prev) => {
        const newTime = prev + SHORT_INTERVAL;
        if (newTime >= MAX_IDLE_TIME) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          handleLogout(true);
        }
        return newTime;
      });
    }, SHORT_INTERVAL);
  };

  useEffect(() => {
    const activityHandler = () => {
      reset();
      setAccumulatedIdleTime(0);
      startInterval();
    };

    window.addEventListener('mousemove', activityHandler);
    window.addEventListener('keydown', activityHandler);
    window.addEventListener('mousedown', activityHandler);
    window.addEventListener('touchstart', activityHandler);
    window.addEventListener('scroll', activityHandler);

    return () => {
      window.removeEventListener('mousemove', activityHandler);
      window.removeEventListener('keydown', activityHandler);
      window.removeEventListener('mousedown', activityHandler);
      window.removeEventListener('touchstart', activityHandler);
      window.removeEventListener('scroll', activityHandler);
    };
  }, [reset]);

  useEffect(() => {
    startInterval();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Auto-logout countdown when modal is shown
  useEffect(() => {
    if (!open) return;

    const deadline = Date.now() + COUNTDOWN_SECONDS * 1000;
    const interval = setInterval(() => {
      const secondsLeft = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemaining(secondsLeft);
      if (secondsLeft <= 0) {
        clearInterval(interval);
        handleLogout(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  // Cross-tab logout sync
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === LocalStorageKeys.UserIdleState && event.newValue === "idle") {
        setOpen(false);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const lastActiveTab = useMemo(() => {
    const val = isLastActiveTab();
    return val === null ? "loading" : val.toString();
  }, [isLastActiveTab]);

  useEffect(() => {
    if (lastActiveTab && !sessionStorage.getItem("loginStatus")) {
      handleLogout(false);
    }
  }, [lastActiveTab]);

  return (
    <>
      <Modal
        id="user_idle-modal"
        show={open}
        animation={false}
        backdropClassName="customDarkModalBackdrop inner in"
        backdrop="static"
        keyboard={false}
        onHide={() => {}}
        centered
      >
        <Modal.Header className="purpleModalHeader font-weight-bold">
          <Modal.Title>Session Timeout</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-3">
            <p>You’ve been inactive. You’ll be logged out in <strong>{remaining} seconds</strong>.</p>
            <p>Select <strong>STAY LOGGED IN</strong> to continue, or <strong>LOGOUT NOW</strong> to end your session immediately.</p>
          </div>
          <hr/>
          <div className="btnActions d-flex">
            <button className="whiteBtnSm" onClick={() => handleLogout(false)}>
              Logout Now
            </button>
            <button className="blueBtnSm" onClick={handleStayLoggedIn}>
              Stay Logged In
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default UserActivity;
